import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject} from "rxjs";
import { map, tap} from "rxjs/operators";
import {GlobalSpinnerService} from "@app/services/misc-services/global-spinner.service";
import {CreditUnion} from "@app/pages/admin/admin.component";
import {CreditUnionService} from "@app/services/credit-union/credit-union.service";
import {Utils} from "@app/utilities/Utils";

@Injectable({
    providedIn: 'root'
})
export class DashboardDataService {
    dashboardFilter: DashboardFilterModel = new DashboardFilterModel;
    downloadDocumentData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    cats: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    cuidCount: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    cuidFilter:DashboardFilterModel = new DashboardFilterModel;
    creditUnions: BehaviorSubject<CreditUnion[]> = new BehaviorSubject<CreditUnion[]>([]);

    constructor(private http: HttpClient, private globalSpinner: GlobalSpinnerService, private creditUnionService: CreditUnionService) {
        this.loadData();
    }
    //gets all the credit unions from the credit union service
    loadCreditUnions() {
        this.creditUnionService.getAllCreditUnions().subscribe((creditUnions: CreditUnion[]) =>
            this.creditUnions.next(creditUnions))
    }
    //loads/filters dashboard data
    loadData() {
        this.globalSpinner.addEvent('loadDashboardData');
        this.http.get(`/api/secured/portalDownload/downloadCount`).pipe(tap((downloads: Array<DownloadDocumentModel>) => {
                let arr = this.formatArrayOfCats(downloads);
                this.cats.next(arr.map(keys => keys.key))
            }
            ),
            map((downloads: Array<DownloadDocumentModel>) => {
                return downloads.filter(download => {
                    if (!this.dashboardFilter.dateRange.start && !this.dashboardFilter.dateRange.end) {
                        return download;
                    }
                    let end = this.setEnd(new Date(this.dashboardFilter?.dateRange?.end));

                    if (new Date(this.dashboardFilter?.dateRange?.start).getTime() <= new Date(download?.downloadDate).getTime()
                        && new Date(download?.downloadDate).getTime() <= end.getTime()) {
                        return download;
                    }
                })
            }), map((downloads: Array<DownloadDocumentModel>) => {
                return downloads.filter(download => {
                    if (!this.dashboardFilter.cat || this.dashboardFilter.cat === 'All') {
                        return download;
                    }
                    if (this.dashboardFilter.cat === download.categoriesTitle) {
                        return download;
                    }
                })
            }), map((downloads: Array<DownloadDocumentModel>) => {
                return downloads.filter(download => {
                    if (!this.dashboardFilter.cuid || this.dashboardFilter.cuid === 'None') {
                        return download;
                    }
                    if (this.dashboardFilter.cuid === download.cuid) {
                        return download;
                    }
                })
            }), map((downloads: Array<DownloadDocumentModel>) => {
                return this.formatArrayOfCats(downloads);

            })).subscribe((downloadDocument: any) => {
            this.downloadDocumentData.next(downloadDocument);
            this.globalSpinner.removeEvent('loadDashboardData');
        }, () => {
            this.globalSpinner.removeEvent('loadDashboardData');
        })
    }
    //formats array for dashbard data
    formatArrayOfCats = (downloads: Array<DownloadDocumentModel>): Array<{ key: string, value: Array<{ file: string; count: number, cat: string }> }> => {
        let arr = [];

        let files: Array<{ file: string; count: number, cat: string }> = []

        downloads.forEach(file => {
            if (!files.find(f => f.file === file.title)) {
                files.push({
                    file: file.title,
                    count: downloads.filter(value => value.title === file.title).length,
                    cat: file.categoriesTitle,
                })
            }
        });
        downloads.map(file => file.categoriesTitle).filter((item, pos, self) => {
            return self.indexOf(item) === pos;
        }).forEach(cat => {
            if (!cat) {
                arr.push({key: 'Uncategorized', value: files.filter(file => !file.cat)})
            } else {
                arr.push({key: cat, value: files.filter(file => cat === file.cat)})
            }
        })
        return arr;
    }
    //loads/filters cuid data
    loadCUIDCount() {
        this.globalSpinner.addEvent('loadCUIDCount');
        return this.http.get(`/api/secured/portalUser/getCuidCount`).pipe(
            map((cuid: Array<DashboardCUIDModel>) => {
                return cuid.filter(dashboardCUID => {
                    if (!this.cuidFilter.dateRange.start && !this.cuidFilter.dateRange.end) {
                        return dashboardCUID;
                    }
                    let end = this.setEnd(new Date(this.cuidFilter?.dateRange?.end));
                    if (new Date(this.cuidFilter?.dateRange?.start).getTime() <= new Date(dashboardCUID?.lastSignin).getTime()
                        && new Date(dashboardCUID?.lastSignin).getTime() <= end.getTime()) {
                        return dashboardCUID;
                    }
                })
            }),
            map((cuid: Array<DashboardCUIDModel>) => {
                return cuid.filter(dashboardCUID => {
                    if (!this.cuidFilter.cuid || this.cuidFilter.cuid === 'None') {
                        return dashboardCUID;
                    }
                    if (this.cuidFilter.cuid === dashboardCUID.cuid) {
                        return dashboardCUID;
                    }
                })
            }),
            map((cuid: Array<DashboardCUIDModel>) => {
                let cuArray = [];
                this.creditUnions.getValue().forEach((cu: CreditUnion) => {
                    const filteredCuInfo = Utils.orderByDate(cuid.filter((item) => item['cuid'] === cu.cuid), 'lastSignin');
                    const lastUser: any = filteredCuInfo[filteredCuInfo.length -1]
                    if(lastUser){
                        cuArray.push({cuid: cu.cuid,logins: filteredCuInfo.length, username: lastUser.username, lastSignin: lastUser.lastSignin});
                    }
                })
                return cuArray;
            })
        )

    }
    //sets the end time to the end of the day
    private setEnd(date: Date) {
        date.setHours(23)
        date.setMinutes(59)
        date.setSeconds(59)
        return date;
    }
}
//RM: There is a document model within the models folder
export class DownloadDocumentModel {
    class: string;
    id: number;
    version: number;
    categories: string;
    categoriesTitle: string;
    cuid: string;
    downloadDate: string;
    title: string;
}

export class DashboardFilterModel {
    dateRange: { start: string; end: string } = {start: '', end: ''};
    cuid: string = '';
    cat: string = ''
}

export class DashboardCUIDModel {
    id: number;
    version: number;
    cuid: string;
    lastSignin: string;
    username: string
}
